import React, { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Heading,
  Text,
  Checkbox,
  Snackbar,
} from "@origin-digital/ods-core";
import {
  getSurveyOptOutStatus,
  updateSurveyOptOutStatus,
} from "src/services/dapi";
import content from "./content.json";
import { reportException } from "@origin-digital/reporting-client";
import { errorTracking } from "src/utils/tracking";

type SnackbarContent = {
  isDisplayed: boolean;
  message?: string;
  tone?: "positive" | "critical";
};

export const EmailNotifications: React.FC = () => {
  const [isSurveyOptIn, setIsSurveyOptIn] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarContent>({
    isDisplayed: false,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [errorState, setErrorState] = useState<Error | null>(null);

  const fetchOptOutStatus = async () => {
    setLoading(true);
    try {
      const surveyOptOut = await getSurveyOptOutStatus();
      setIsSurveyOptIn(!surveyOptOut);
    } catch (error) {
      setErrorState(error as Error);
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyOptOutChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setIsSurveyOptIn(checked);
    setUpdating(true);
    try {
      await updateSurveyOptOutStatus(!checked);
      setSnackbar({
        isDisplayed: true,
        message: content.success,
        tone: "positive",
      });
    } catch (error) {
      setIsSurveyOptIn((prev) => !prev);
      setSnackbar({
        isDisplayed: true,
        message: content.error,
        tone: "critical",
      });
      errorTracking(error as Error);
      reportException(
        `UpdateContactPreferencesScreen.updateEmailSurveyOptOut()`,
        error as Error
      );
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    fetchOptOutStatus();
  }, []);

  if (errorState) {
    throw errorState;
  }

  return (
    <>
      <Card>
        <Stack>
          <Heading variant="h3">Email notifications</Heading>
          <Text>
            Choose the type of email notifications you want to receive from
            Origin.
          </Text>
          <Checkbox
            label="Origin Internet surveys"
            helperText="Email surveys sent after you’ve been in contact with one of our Internet specialists."
            id="email-survey-checkbox"
            checked={isSurveyOptIn}
            onChange={handleSurveyOptOutChange}
            disabled={loading || updating}
          />
        </Stack>
      </Card>
      <Snackbar
        data-id="contact-preference-snackbar"
        open={snackbar.isDisplayed}
        tone={snackbar.tone}
        align="center"
        showClose
        autoHideDuration={5000}
        onClose={() =>
          setSnackbar((prevState) => ({
            ...prevState,
            isDisplayed: false,
          }))
        }
      >
        {snackbar.message}
      </Snackbar>
    </>
  );
};
